/**
 * MobilePageHeader — Shared header tile for league and team pages
 * Edge-to-edge, flush against sticky nav, compact, no rounded corners
 * pageBadge renders top-center. Inline dropdown on subtitle line far-right.
 */
import React from 'react';
import { dynastyTheme, withAlpha } from '../../../services/colorService';
import { mobileNavStyle } from '../../../services/mobileStyleService';

const C = dynastyTheme.tokens.colors;

const MobilePageHeader = ({
  title,
  subtitle,
  pageBadge,
  bannerUrl,
  logoUrl,
  logoFallbackText,
  children,
  showLogo = true,
  inlineDropdown,
}) => {
  const fallbackChar = (logoFallbackText || title || 'D')[0].toUpperCase();

  return (
    <div style={{
      position: 'relative',
      overflow: 'hidden',
      ...mobileNavStyle.container,
      margin: 0,
      // No bottom margin: the section below (e.g. Daily Category Rankings) owns its
      // own 6px top gap via its header padding — a banner margin here stacked on top
      // of that and pushed the first section label too far down.
      marginBottom: 0,
      borderRadius: 0,
    }}>
      {/* Banner background */}
      {bannerUrl && (
        <>
          <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
            <img
              src={bannerUrl}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.8 }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <div style={{
            position: 'absolute', inset: 0, zIndex: 1,
            background: `linear-gradient(to right, ${withAlpha(C.black, 0.75)}, ${withAlpha(C.black, 0.4)})`,
          }} />
        </>
      )}

      {/* Content row: logo + title/subtitle/dropdown */}
      <div style={{
        position: 'relative', zIndex: 2,
        display: 'flex', alignItems: 'center', gap: '10px',
        // 4px bottom: a little photo space below the subtitle; with the outer
        // marginBottom at 0, the banner's bottom content sits a touch above the first
        // section label (which keeps its own 6px header top-padding).
        padding: '6px 16px 4px',
      }}>
        {/* Page badge — absolute top-right, zero layout impact */}
        {pageBadge && (
          <span style={{
            position: 'absolute', top: '4px', right: '16px',
            fontSize: dynastyTheme.tokens.fontSize['2xs'],
            fontWeight: '700',
            color: dynastyTheme.tokens.colors.primary,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            {pageBadge}
          </span>
        )}
        {/* Logo */}
        {showLogo && (logoUrl ? (
          <img
            src={logoUrl}
            alt=""
            style={{
              width: '44px', height: '44px', borderRadius: '6px',
              objectFit: 'cover',
              border: `1.5px solid ${dynastyTheme.tokens.colors.primary}`,
              flexShrink: 0,
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div style={{
            width: '44px', height: '44px', borderRadius: '6px',
            backgroundColor: dynastyTheme.tokens.colors.primary + '25',
            border: `1.5px solid ${dynastyTheme.tokens.colors.primary}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '16px', fontWeight: '700',
            color: dynastyTheme.tokens.colors.primary,
            flexShrink: 0,
          }}>
            {fallbackChar}
          </div>
        ))}

        {/* Title + subtitle line with inline dropdown pushed right */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{
            fontSize: dynastyTheme.tokens.fontSize.lg,
            fontWeight: '700',
            color: dynastyTheme.tokens.colors.neutral[50],
            margin: 0,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            textShadow: bannerUrl ? `1px 1px 3px ${withAlpha(C.black, 0.8)}` : 'none',
            lineHeight: '1.2',
          }}>
            {title}
          </h1>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            marginTop: '1px', overflow: 'hidden', height: '18px',
          }}>
            {subtitle && (
              <span style={{
                fontSize: dynastyTheme.tokens.fontSize.xs,
                color: dynastyTheme.tokens.colors.primary,
                fontWeight: '600',
                textShadow: bannerUrl ? `1px 1px 2px ${withAlpha(C.black, 0.8)}` : 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}>
                {subtitle}
              </span>
            )}
            {inlineDropdown && (
              <div style={{ marginLeft: 'auto', flexShrink: 1, minWidth: 0, maxWidth: '40%' }}>
                {inlineDropdown}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Optional children below */}
      {children && (
        <div style={{ position: 'relative', zIndex: 2, padding: '4px 16px 6px' }}>
          {children}
        </div>
      )}
    </div>
  );
};

export default MobilePageHeader;
